const API_URL = process.env.REACT_APP_API_BASE_URL || '';
const BASE = `${API_URL}/api/v1/metrics`;

/**
 * Business metrics. Admin only — every endpoint answers 403 for other roles.
 *
 * Money arrives as integer centavos; divide by 100 for pesos (see centsToPesos).
 * Ratios arrive as 0–1 decimals or `null`, and `null` means "cannot be
 * computed", never zero: the backend has no capacity data, so occupancy and
 * utilization denominators do not exist. Each response carries a `limitations`
 * array explaining its own gaps — show it rather than hiding it.
 */
function queryString(params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) value.forEach(v => search.append(key, v));
        else search.append(key, value);
    });
    return search.toString();
}

/**
 * Metrics errors carry { error: { code, message, field } }, unlike the rest of
 * the API. The code is what callers should branch on, so it is preserved on the
 * thrown error instead of being flattened into a string.
 */
async function getJson(path, params, authorizedFetch) {
    const query = queryString(params);
    const res = await authorizedFetch(`${BASE}${path}${query ? `?${query}` : ''}`);
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const err = new Error(body?.error?.message || `No se pudo cargar (${res.status})`);
        err.code = body?.error?.code || 'UNKNOWN';
        err.field = body?.error?.field;
        err.status = res.status;
        throw err;
    }
    return res.json();
}

export class MetricsService {

    /** KPI tiles for a range. The only call a dashboard home should make. */
    static getSummary({ from, to }, authorizedFetch) {
        return getJson('/summary', { from, to }, authorizedFetch);
    }

    /** Recognized on the sale date, per bucket and line. */
    static getRevenue({ from, to, granularity = 'month', line }, authorizedFetch) {
        return getJson('/revenue', { from, to, granularity, line }, authorizedFetch);
    }

    /** The cash view: what was collected, by payment date and method. */
    static getPayments({ from, to, granularity = 'month' }, authorizedFetch) {
        return getJson('/payments', { from, to, granularity }, authorizedFetch);
    }

    static getActiveClients({ asOf, windowDays }, authorizedFetch) {
        return getJson('/clients/active', { asOf, windowDays }, authorizedFetch);
    }

    static getRetention({ asOf, period = 'month' }, authorizedFetch) {
        return getJson('/clients/retention', { asOf, period }, authorizedFetch);
    }

    static getCrossSell({ asOf, windowDays }, authorizedFetch) {
        return getJson('/clients/cross-sell', { asOf, windowDays }, authorizedFetch);
    }

    static getHotelOccupancy({ from, to, granularity = 'month' }, authorizedFetch) {
        return getJson('/hotel/occupancy', { from, to, granularity }, authorizedFetch);
    }

    static getDaycareUtilization({ from, to, granularity = 'month' }, authorizedFetch) {
        return getJson('/daycare/utilization', { from, to, granularity }, authorizedFetch);
    }

    static getGroomingUtilization({ from, to, granularity = 'month' }, authorizedFetch) {
        return getJson('/grooming/utilization', { from, to, granularity }, authorizedFetch);
    }

    /** Outstanding cashback in centavos, unused packages in units. */
    static getLiability({ asOf }, authorizedFetch) {
        return getJson('/liability', { asOf }, authorizedFetch);
    }
}

export const centsToPesos = (cents) => (cents ?? 0) / 100;

/** Ratio (0–1) as a percentage label; '—' when the metric cannot be computed. */
export const formatRatio = (ratio, places = 1) =>
    (ratio === null || ratio === undefined ? '—' : `${(ratio * 100).toFixed(places)}%`);

export const LINE_LABELS = {
    daycare: 'Guardería',
    hotel: 'Hotel',
    vet: 'Veterinaria',
    vaccine: 'Vacunas',
    grooming: 'Estética',
    other: 'Otros',
};

export const METHOD_LABELS = {
    Cash: 'Efectivo',
    Card: 'Tarjeta',
    Bit: 'Bit',
    Transfer: 'Transferencia',
    Cashback: 'Cashback',
};
